import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Use sessionStorage instead of localStorage for better security
// Session data is cleared when browser tab closes
const SESSION_KEYS = {
  TOKEN: 'auth_token',
  USER: 'auth_user',
};

// Helper to safely get session data
const getSessionItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSessionItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    console.warn('Failed to save to session storage');
  }
};

const removeSessionItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore
  }
};

const getSessionJSON = (key) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getSessionItem(SESSION_KEYS.TOKEN);
    const userData = getSessionJSON(SESSION_KEYS.USER);
    
    if (token && userData) {
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier, password) => {
    const response = await axios.post(`${API}/auth/login`, { identifier, password });
    const { token, user: userData } = response.data;
    
    setSessionItem(SESSION_KEYS.TOKEN, token);
    setSessionItem(SESSION_KEYS.USER, JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    removeSessionItem(SESSION_KEYS.TOKEN);
    removeSessionItem(SESSION_KEYS.USER);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const isAdmin = useCallback(() => user?.role === 'admin', [user]);
  const isMember = useCallback(() => user?.role === 'member', [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, isMember, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
